import type { FrameOf } from "../protocol/protocol";

export interface LivePart {
  kind: "text" | "reasoning";
  text: string;
}

/**
 * The assistant message a run is streaming, shown as `run:<id>:live` until the step's message
 * lands. `gap` means we joined late or missed a delta: show the typing indicator until then.
 */
export interface LiveMessage {
  runId: number;
  /** By the part index of the model response; tool calls take indexes too, so there are holes. */
  parts: Record<number, LivePart>;
  toolCallIds: string[];
  gap: boolean;
  lastDeltaAt: number;
}

export function newLiveMessage(runId: number, now: number, gap: boolean): LiveMessage {
  return { runId, parts: {}, toolCallIds: [], gap, lastDeltaAt: now };
}

/** Clears what the persisted step replaced; the next model response starts its indexes at 0. */
export function resetLiveStep(live: LiveMessage): void {
  live.parts = {};
  live.toolCallIds = [];
  live.gap = false;
}

/** Applies one delta in place. Offsets are UTF-16 units, the same unit as a JS string's length. */
export function applyDelta(live: LiveMessage, delta: FrameOf<"message.delta">, now: number): void {
  live.lastDeltaAt = now;
  if (live.gap) return;
  const part = live.parts[delta.index];
  const length = part?.text.length ?? 0;
  if (delta.offset > length) {
    live.gap = true;
    return;
  }
  // An offset below our length is a repeat: keep only the part we have not seen.
  const fresh = delta.content.slice(length - delta.offset);
  if (part) part.text += fresh;
  else live.parts[delta.index] = { kind: delta.part_kind, text: fresh };
}

export function liveParts(live: LiveMessage): LivePart[] {
  return Object.keys(live.parts)
    .map(Number)
    .sort((a, b) => a - b)
    .map((index) => live.parts[index]);
}
